import React from "react";
import { Box, Container, Typography, Paper, Chip, Divider, Grid, Link as MuiLink } from "@mui/material";
import {
  BRAND,
  Section,
  Callout,
  MiniHeader,
  MiniFooter,
  bodyText,
  subHeading,
  listSx,
  usePublicNav,
  useContactEmail,
} from "components/PublicLayout";

const EFFECTIVE_DATE = "21 maggio 2026";

const SECTIONS = [
  { id: "introduzione", title: "1. Introduzione" },
  { id: "titolare", title: "2. Titolare del Trattamento" },
  { id: "base-giuridica", title: "3. Base Giuridica Svizzera" },
  { id: "dati-raccolti", title: "4. Dati che Raccogliamo" },
  { id: "pazienti", title: "5. Identificazione dei Pazienti" },
  { id: "finalita", title: "6. Finalità del Trattamento" },
  { id: "pagamenti", title: "7. Fatturazione e Pagamenti" },
  { id: "permessi", title: "8. Permessi dell'App" },
  { id: "terze-parti", title: "9. Servizi di Terze Parti" },
  { id: "condivisione", title: "10. Condivisione dei Dati" },
  { id: "trasferimenti", title: "11. Trasferimenti Internazionali" },
  { id: "conservazione", title: "12. Conservazione dei Dati" },
  { id: "sicurezza", title: "13. Sicurezza dei Dati" },
  { id: "diritti", title: "14. I Tuoi Diritti (nLPD)" },
  { id: "eliminazione", title: "15. Eliminazione dell'Account" },
  { id: "minori", title: "16. Privacy dei Minori" },
  { id: "cookie", title: "17. Cookie" },
  { id: "modifiche", title: "18. Modifiche all'Informativa" },
  { id: "contatti", title: "19. Contatti" },
];

export default function PrivacyPolicy() {
  const goTo = usePublicNav();
  const contactEmail = useContactEmail();

  return (
    <Box sx={{ bgcolor: BRAND.bgSoft, minHeight: "100vh", fontFamily: '"Inter", sans-serif' }}>
      <MiniHeader
        onHome={() => goTo("/")}
        secondaryLabel="Termini"
        secondaryHref="/term-condition"
      />

      {/* Hero */}
      <Box
        sx={{
          position: "relative",
          pt: { xs: "96px", md: "120px" },
          pb: { xs: 5, md: 8 },
          background: `linear-gradient(180deg, #ffffff 0%, ${BRAND.bgSoft} 100%)`,
          borderBottom: `1px solid ${BRAND.border}`,
          overflow: "hidden",
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: -120,
            right: -120,
            width: 360,
            height: 360,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${BRAND.teal}33 0%, transparent 70%)`,
            filter: "blur(20px)",
            pointerEvents: "none",
          }}
        />
        <Container maxWidth="md" sx={{ position: "relative" }}>
          <Chip
            label="Informativa sulla privacy"
            sx={{
              bgcolor: `${BRAND.teal}22`,
              color: BRAND.tealDark,
              fontWeight: 600,
              fontSize: "0.78rem",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              border: `1px solid ${BRAND.teal}55`,
              mb: 2,
            }}
          />
          <Typography
            component="h1"
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontWeight: 800,
              color: BRAND.ink,
              letterSpacing: "-0.025em",
              fontSize: { xs: "2rem", md: "2.75rem" },
              lineHeight: 1.15,
              mb: 1.5,
            }}
          >
            Privacy Policy di MedLift
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              color: BRAND.muted,
              fontSize: { xs: "1rem", md: "1.125rem" },
              lineHeight: 1.6,
              maxWidth: 720,
              mb: 2,
            }}
          >
            MedLift opera sotto la giurisdizione svizzera. Spieghiamo in modo
            trasparente quali dati raccogliamo, perché li usiamo, con chi li
            condividiamo e come puoi esercitare i tuoi diritti ai sensi della
            Legge Federale svizzera sulla Protezione dei Dati (nLPD del
            1° settembre 2023).
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
            <Chip
              label={`Ultimo aggiornamento: ${EFFECTIVE_DATE}`}
              sx={{
                bgcolor: BRAND.bgPanel,
                color: BRAND.inkSoft,
                border: `1px solid ${BRAND.border}`,
                fontWeight: 500,
                fontSize: "0.8rem",
              }}
            />
            <Chip
              label="Conforme nLPD svizzera"
              sx={{
                bgcolor: BRAND.bgPanel,
                color: BRAND.inkSoft,
                border: `1px solid ${BRAND.border}`,
                fontWeight: 500,
                fontSize: "0.8rem",
              }}
            />
          </Box>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Grid container spacing={{ xs: 3, md: 5 }}>
          {/* TOC */}
          <Grid item xs={12} md={4} lg={3}>
            <Box sx={{ position: { md: "sticky" }, top: { md: 88 } }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  border: `1px solid ${BRAND.border}`,
                  bgcolor: BRAND.bgPanel,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontWeight: 700,
                    color: BRAND.ink,
                    fontSize: "0.85rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    mb: 1.5,
                  }}
                >
                  Indice
                </Typography>
                <Box component="nav" aria-label="Indice della privacy policy">
                  {SECTIONS.map((s) => (
                    <MuiLink
                      key={s.id}
                      href={`#${s.id}`}
                      underline="none"
                      sx={{
                        display: "block",
                        py: 0.6,
                        px: 1,
                        borderRadius: 1.5,
                        color: BRAND.body,
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        transition: "all 0.15s ease",
                        "&:hover": {
                          bgcolor: `${BRAND.teal}18`,
                          color: BRAND.ink,
                        },
                      }}
                    >
                      {s.title}
                    </MuiLink>
                  ))}
                </Box>
              </Paper>
            </Box>
          </Grid>

          {/* Body */}
          <Grid item xs={12} md={8} lg={9}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 5 },
                borderRadius: 4,
                border: `1px solid ${BRAND.border}`,
                bgcolor: BRAND.bgPanel,
              }}
            >
              <Section id="introduzione" title="1. Introduzione">
                <Typography sx={bodyText}>
                  La presente informativa descrive le pratiche di MedLift in
                  materia di raccolta, utilizzo, conservazione e protezione dei
                  dati personali degli utenti dell'app mobile MedLift
                  (disponibile su Apple App Store e Google Play Store) e del
                  sito web{" "}
                  <MuiLink
                    href="https://medlift.app"
                    sx={{ color: BRAND.tealDark, fontWeight: 600 }}
                  >
                    medlift.app
                  </MuiLink>{" "}
                  (collettivamente, il "Servizio").
                </Typography>
                <Typography sx={bodyText}>
                  MedLift è una piattaforma svizzera che mette in contatto
                  strutture sanitarie (ospedali, cliniche, RSA) con
                  trasportatori sanitari professionisti per organizzare il
                  trasporto non urgente di pazienti. Utilizzando il Servizio
                  accetti le pratiche descritte in questa informativa.
                </Typography>
              </Section>

              <Section id="titolare" title="2. Titolare del Trattamento">
                <Typography sx={bodyText}>
                  Il Titolare del trattamento dei dati personali è{" "}
                  <strong>MedLift</strong>, con sede in Svizzera. Puoi
                  contattarci per qualsiasi questione relativa al trattamento
                  dei tuoi dati personali:
                </Typography>
                <Box component="ul" sx={listSx}>
                  <li>
                    Email:{" "}
                    <MuiLink
                      href={`mailto:${contactEmail}`}
                      sx={{ color: BRAND.tealDark, fontWeight: 600 }}
                    >
                      {contactEmail}
                    </MuiLink>
                  </li>
                  <li>Sito web: medlift.app</li>
                </Box>
              </Section>

              <Section id="base-giuridica" title="3. Base Giuridica Svizzera">
                <Typography sx={bodyText}>
                  MedLift opera sotto la giurisdizione della{" "}
                  <strong>Confederazione Svizzera</strong>. Il trattamento dei
                  dati personali avviene in conformità con:
                </Typography>
                <Box component="ul" sx={listSx}>
                  <li>
                    La <strong>nuova Legge Federale sulla Protezione dei Dati
                    (nLPD)</strong>, in vigore dal 1° settembre 2023.
                  </li>
                  <li>
                    L'<strong>Ordinanza sulla Protezione dei Dati (OPDa)</strong>{" "}
                    del Consiglio federale.
                  </li>
                  <li>
                    Il <strong>Codice delle Obbligazioni (CO)</strong> per gli
                    aspetti contrattuali e fatturali.
                  </li>
                </Box>
                <Typography sx={bodyText}>
                  L'autorità di controllo competente è l'
                  <strong>
                    Incaricato Federale della Protezione dei Dati e della
                    Trasparenza (IFPDT / EDÖB)
                  </strong>
                  , presso il quale puoi presentare reclamo. Foro competente:
                  Svizzera. Per gli utenti residenti nell'Unione Europea
                  applichiamo, ove richiesto, anche le disposizioni equivalenti
                  del Regolamento UE 2016/679 (GDPR).
                </Typography>
              </Section>

              <Section id="dati-raccolti" title="4. Dati che Raccogliamo">
                <Typography sx={bodyText}>
                  Raccogliamo solo i dati strettamente necessari per fornire il
                  Servizio (principio di minimizzazione, art. 6 nLPD).
                </Typography>

                <Typography sx={subHeading}>4.1 Dati di account</Typography>
                <Box component="ul" sx={listSx}>
                  <li>Nome e cognome del referente professionale</li>
                  <li>Indirizzo email</li>
                  <li>Numero di telefono</li>
                  <li>
                    Ragione sociale, indirizzo e dati fiscali della struttura
                    sanitaria o dell'attività di trasporto
                  </li>
                  <li>
                    Foto profilo (opzionale) e documenti professionali del
                    personale di trasporto (patente, assicurazione veicolo,
                    libretto di circolazione)
                  </li>
                  <li>Credenziali di accesso (memorizzate in forma criptata)</li>
                </Box>

                <Typography sx={subHeading}>4.2 Dati delle prenotazioni</Typography>
                <Box component="ul" sx={listSx}>
                  <li>Punto di prelievo e destinazione del trasporto</li>
                  <li>Data e orario richiesti</li>
                  <li>
                    Ausili medici richiesti (es. barella, sedia a rotelle,
                    ossigeno)
                  </li>
                  <li>
                    <strong>Codice interno paziente</strong> assegnato dalla
                    struttura — mai il nome o dati anagrafici (vedi Sez. 5)
                  </li>
                  <li>Note operative inserite dal richiedente</li>
                </Box>

                <Typography sx={subHeading}>4.3 Dati di geolocalizzazione</Typography>
                <Box component="ul" sx={listSx}>
                  <li>
                    Posizione GPS del veicolo durante una corsa attiva, per
                    consentire al richiedente di seguire il tragitto in tempo
                    reale
                  </li>
                  <li>
                    Posizione approssimata per calcolare i trasportatori
                    disponibili nelle vicinanze
                  </li>
                  <li>
                    Posizione in background (solo per il ruolo trasportatore e
                    solo durante corse accettate, con notifica persistente
                    visibile)
                  </li>
                </Box>

                <Typography sx={subHeading}>4.4 Dati tecnici e di utilizzo</Typography>
                <Box component="ul" sx={listSx}>
                  <li>
                    Identificatori dispositivo, modello, sistema operativo e
                    versione app
                  </li>
                  <li>Indirizzo IP e dati di connessione</li>
                  <li>Token di notifica push (Apple APNS / Firebase FCM)</li>
                  <li>
                    Log di errore e diagnostica per stabilità e miglioramento
                    dell'app
                  </li>
                </Box>
              </Section>

              <Section id="pazienti" title="5. Identificazione dei Pazienti">
                <Callout tone="info" title="Principio di pseudonimizzazione">
                  MedLift <strong>non raccoglie e non memorizza nomi,
                  cognomi, codici fiscali, numeri di assicurazione, dati
                  clinici o qualsiasi altra informazione anagrafica o sanitaria
                  dei pazienti</strong>.
                </Callout>
                <Typography sx={bodyText}>
                  Ogni paziente viene identificato esclusivamente tramite un{" "}
                  <strong>codice interno alfanumerico</strong> generato dalla
                  struttura sanitaria richiedente (per esempio:{" "}
                  <em>RX-2026-0142</em>). La corrispondenza tra codice interno
                  e identità del paziente resta sotto il controllo esclusivo
                  della struttura sanitaria, all'interno dei propri sistemi
                  protetti, e non viene mai trasferita a MedLift.
                </Typography>
                <Typography sx={bodyText}>Di conseguenza:</Typography>
                <Box component="ul" sx={listSx}>
                  <li>
                    Nei database di MedLift non esistono dati personali
                    riconducibili a un paziente specifico.
                  </li>
                  <li>
                    Il trasportatore riceve solo il codice interno, il punto di
                    prelievo, la destinazione, l'orario e gli ausili medici
                    richiesti.
                  </li>
                  <li>
                    Nessun dato clinico, diagnosi o motivo del trasporto viene
                    raccolto o condiviso tramite la piattaforma.
                  </li>
                  <li>
                    Questa pseudonimizzazione strutturale riduce drasticamente
                    il rischio per gli interessati ed è coerente con l'art. 6
                    cpv. 5 nLPD.
                  </li>
                </Box>
              </Section>

              <Section id="finalita" title="6. Finalità del Trattamento">
                <Typography sx={bodyText}>
                  Trattiamo i dati personali per le seguenti finalità, indicate
                  con la relativa base giuridica ai sensi della nLPD:
                </Typography>
                <Box component="ul" sx={listSx}>
                  <li>
                    <strong>Esecuzione del contratto</strong> (art. 31 cpv. 2
                    let. a nLPD): creazione account, gestione prenotazioni,
                    abbinamento richiesta/trasportatore, geolocalizzazione
                    corsa attiva.
                  </li>
                  <li>
                    <strong>Obblighi di legge</strong> (art. 31 cpv. 1 nLPD):
                    conservazione documenti contabili e fiscali per gli
                    obblighi previsti dal Codice delle Obbligazioni.
                  </li>
                  <li>
                    <strong>Interesse legittimo preponderante</strong> (art. 31
                    cpv. 2 let. b–c nLPD): sicurezza della piattaforma,
                    prevenzione frodi e abusi, diagnostica tecnica in forma
                    anonima.
                  </li>
                  <li>
                    <strong>Consenso</strong> (art. 6 cpv. 6 nLPD):
                    comunicazioni di marketing diretto, raccolta di dati
                    facoltativi (es. foto profilo). Revocabile in qualsiasi
                    momento.
                  </li>
                </Box>
              </Section>

              <Section id="pagamenti" title="7. Fatturazione e Pagamenti">
                <Callout tone="info" title="Nessun pagamento all'interno dell'app">
                  MedLift <strong>non elabora pagamenti reali</strong>. La
                  piattaforma non integra Stripe, PayPal, carte di credito o
                  altri provider di pagamento. Non transitano sui nostri
                  server numeri di carta né credenziali bancarie.
                </Callout>
                <Typography sx={bodyText}>
                  Gli importi che vedi nell'app hanno una funzione esclusivamente{" "}
                  <strong>contabile e di rendicontazione interna</strong>:
                  servono alle strutture sanitarie per tenere traccia del
                  fatturato generato e ai trasportatori per monitorare i
                  compensi maturati.
                </Typography>
                <Typography sx={bodyText}>
                  Il regolamento finanziario avviene{" "}
                  <strong>direttamente tra struttura e trasportatore al di
                  fuori della piattaforma</strong>, tipicamente tramite bonifico
                  bancario, secondo i termini contrattuali concordati
                  bilateralmente. MedLift non riceve, custodisce né intermedia
                  alcun importo monetario.
                </Typography>
                <Typography sx={bodyText}>
                  Conseguenze sul piano della privacy:
                </Typography>
                <Box component="ul" sx={listSx}>
                  <li>
                    Non raccogliamo dati finanziari sensibili (numeri di carta,
                    IBAN, codici CVV).
                  </li>
                  <li>
                    Conserviamo solo gli importi virtuali associati a ciascuna
                    prenotazione per consentire la rendicontazione interna.
                  </li>
                  <li>
                    Le fatture emesse tra struttura e trasportatore restano
                    interamente sotto la responsabilità delle parti coinvolte.
                  </li>
                </Box>
              </Section>

              <Section id="permessi" title="8. Permessi dell'App">
                <Typography sx={bodyText}>
                  Per funzionare correttamente, l'app MedLift richiede l'accesso
                  ad alcune funzionalità del dispositivo. Ogni permesso è
                  richiesto solo se necessario alla funzione corrispondente e
                  può essere revocato in qualsiasi momento dalle impostazioni
                  del dispositivo.
                </Typography>
                <Box component="ul" sx={listSx}>
                  <li>
                    <strong>Posizione (in uso e in background)</strong> — per
                    mostrare la posizione del veicolo durante una corsa attiva
                    e abbinare le richieste ai trasportatori più vicini. Per i
                    trasportatori l'accesso in background è limitato alla
                    durata della corsa accettata, con notifica visibile.
                  </li>
                  <li>
                    <strong>Notifiche push</strong> — per avvisarti di nuove
                    richieste, accettazioni, arrivo del trasportatore o
                    aggiornamenti di stato.
                  </li>
                  <li>
                    <strong>Fotocamera e libreria foto</strong> — solo se
                    scegli di caricare la foto profilo o un documento richiesto
                    (es. patente del trasportatore).
                  </li>
                  <li>
                    <strong>Microfono</strong> — solo se utilizzi la chat
                    vocale interna (funzione opzionale).
                  </li>
                  <li>
                    <strong>Rete</strong> — necessaria per la comunicazione con
                    i nostri server.
                  </li>
                </Box>
              </Section>

              <Section id="terze-parti" title="9. Servizi di Terze Parti">
                <Typography sx={bodyText}>
                  Per fornire il Servizio ci affidiamo a fornitori selezionati,
                  vincolati da contratti che impongono adeguate garanzie sulla
                  protezione dei dati. Non utilizziamo provider di pagamento.
                </Typography>
                <Box component="ul" sx={listSx}>
                  <li>
                    <strong>Google Firebase</strong> (Google LLC) —
                    autenticazione, database in tempo reale, archiviazione
                    file, notifiche push, hosting.
                  </li>
                  <li>
                    <strong>Google Maps Platform</strong> — calcolo percorsi,
                    distanze e visualizzazione mappa.
                  </li>
                  <li>
                    <strong>Stream Chat</strong> — messaggistica in tempo reale
                    tra struttura e trasportatore.
                  </li>
                  <li>
                    <strong>Expo / Apple APNS</strong> — invio notifiche push
                    su dispositivi iOS e Android.
                  </li>
                  <li>
                    <strong>Apple Sign In / Google Sign In</strong> — accesso
                    semplificato tramite account esistente, se scegli di
                    utilizzarli.
                  </li>
                </Box>
              </Section>

              <Section id="condivisione" title="10. Condivisione dei Dati">
                <Typography sx={bodyText}>
                  Non vendiamo né cediamo i tuoi dati personali a terzi per
                  finalità commerciali. Condividiamo i dati solo nei seguenti
                  casi:
                </Typography>
                <Box component="ul" sx={listSx}>
                  <li>
                    Con il trasportatore o la struttura sanitaria controparte
                    della prenotazione, limitatamente alle informazioni
                    necessarie a completare il servizio (nome del referente
                    professionale, indirizzi, recapito telefonico, codice
                    interno del paziente).
                  </li>
                  <li>
                    Con i fornitori tecnologici elencati alla sezione 9, in
                    qualità di responsabili del trattamento.
                  </li>
                  <li>
                    Con le autorità svizzere competenti, qualora richiesto da
                    norme di legge o da provvedimenti giudiziari.
                  </li>
                </Box>
              </Section>

              <Section id="trasferimenti" title="11. Trasferimenti Internazionali">
                <Typography sx={bodyText}>
                  Alcuni dei nostri fornitori tecnologici (es. Google) hanno
                  sede fuori dalla Svizzera. In tali casi i trasferimenti
                  avvengono in presenza di adeguate garanzie ai sensi
                  dell'art. 16 nLPD:
                </Typography>
                <Box component="ul" sx={listSx}>
                  <li>
                    Trasferimento verso Paesi riconosciuti dal Consiglio
                    federale come dotati di un livello di protezione adeguato.
                  </li>
                  <li>
                    In assenza di adeguatezza, applicazione di clausole
                    contrattuali standard riconosciute dall'IFPDT.
                  </li>
                </Box>
              </Section>

              <Section id="conservazione" title="12. Conservazione dei Dati">
                <Typography sx={bodyText}>
                  Conserviamo i dati solo per il tempo necessario alle finalità
                  per cui sono stati raccolti:
                </Typography>
                <Box component="ul" sx={listSx}>
                  <li>
                    Dati di account: per tutta la durata del rapporto e fino a
                    12 mesi dopo la cancellazione, salvo richiesta di
                    eliminazione anticipata.
                  </li>
                  <li>
                    Documenti contabili e fatturali (anche se virtuali nella
                    piattaforma): 10 anni, ai sensi dell'art. 958f del Codice
                    delle Obbligazioni.
                  </li>
                  <li>
                    Dati di geolocalizzazione delle corse: 12 mesi per
                    risoluzione contestazioni, poi anonimizzati.
                  </li>
                  <li>
                    Codici interni paziente associati a una prenotazione: stessa
                    durata della prenotazione di riferimento, poi cancellati.
                  </li>
                  <li>Log tecnici e diagnostici: massimo 90 giorni.</li>
                </Box>
              </Section>

              <Section id="sicurezza" title="13. Sicurezza dei Dati">
                <Typography sx={bodyText}>
                  Adottiamo misure tecniche e organizzative appropriate ai
                  sensi dell'art. 8 nLPD per proteggere i dati da accessi non
                  autorizzati, perdita, alterazione o divulgazione:
                </Typography>
                <Box component="ul" sx={listSx}>
                  <li>Trasmissione cifrata in HTTPS/TLS</li>
                  <li>
                    Archiviazione su infrastrutture cloud certificate (ISO
                    27001, SOC 2)
                  </li>
                  <li>Password salvate con hashing crittografico</li>
                  <li>Accesso ai dati limitato al personale autorizzato</li>
                  <li>Backup regolari e procedure di disaster recovery</li>
                  <li>
                    Pseudonimizzazione strutturale dei dati paziente (cfr. Sez. 5)
                  </li>
                </Box>
              </Section>

              <Section id="diritti" title="14. I Tuoi Diritti (nLPD)">
                <Typography sx={bodyText}>
                  Ai sensi della nLPD hai in qualsiasi momento il diritto di:
                </Typography>
                <Box component="ul" sx={listSx}>
                  <li>
                    <strong>Accedere</strong> ai tuoi dati personali e ricevere
                    una copia (art. 25 nLPD).
                  </li>
                  <li>
                    <strong>Rettificare</strong> dati inesatti o incompleti
                    (art. 32 nLPD).
                  </li>
                  <li>
                    <strong>Ottenere la cancellazione</strong> dei dati che non
                    sono più necessari.
                  </li>
                  <li>
                    <strong>Limitare o opporti</strong> al trattamento.
                  </li>
                  <li>
                    <strong>Portabilità</strong> dei dati in formato elettronico
                    strutturato (art. 28 nLPD).
                  </li>
                  <li>
                    <strong>Revocare il consenso</strong> in qualsiasi momento,
                    senza pregiudicare la liceità del trattamento precedente.
                  </li>
                  <li>
                    Presentare <strong>reclamo all'IFPDT</strong> (Incaricato
                    Federale della Protezione dei Dati e della Trasparenza,{" "}
                    <MuiLink
                      href="https://www.edoeb.admin.ch"
                      sx={{ color: BRAND.tealDark, fontWeight: 600 }}
                    >
                      edoeb.admin.ch
                    </MuiLink>
                    ).
                  </li>
                </Box>
                <Typography sx={bodyText}>
                  Per esercitare i tuoi diritti scrivici a{" "}
                  <MuiLink
                    href={`mailto:${contactEmail}`}
                    sx={{ color: BRAND.tealDark, fontWeight: 600 }}
                  >
                    {contactEmail}
                  </MuiLink>
                  . Risponderemo entro 30 giorni.
                </Typography>
              </Section>

              <Section id="eliminazione" title="15. Eliminazione dell'Account">
                <Callout tone="info" title="Come cancellare l'account">
                  Puoi eliminare il tuo account in qualsiasi momento
                  direttamente dall'app MedLift:
                  <br />
                  <strong>Profilo → Impostazioni account → Elimina account</strong>
                  <br />
                  In alternativa puoi richiedere la cancellazione scrivendo a{" "}
                  <MuiLink
                    href={`mailto:${contactEmail}`}
                    sx={{ color: BRAND.tealDark, fontWeight: 600 }}
                  >
                    {contactEmail}
                  </MuiLink>
                  : daremo seguito alla richiesta entro 30 giorni.
                </Callout>
                <Typography sx={bodyText}>
                  Una volta confermata la cancellazione:
                </Typography>
                <Box component="ul" sx={listSx}>
                  <li>
                    Vengono rimossi il profilo, l'email, il numero di telefono,
                    la foto profilo e l'accesso tramite Google o Apple.
                  </li>
                  <li>
                    Vengono anonimizzati i dati delle corse pregresse, in modo
                    che non siano più riconducibili alla tua identità.
                  </li>
                  <li>
                    Vengono conservati esclusivamente i documenti contabili per
                    la durata prevista dal Codice delle Obbligazioni (10 anni).
                  </li>
                </Box>
                <Typography sx={bodyText}>
                  La cancellazione è irreversibile: una volta eliminato
                  l'account i dati non possono essere recuperati.
                </Typography>
              </Section>

              <Section id="minori" title="16. Privacy dei Minori">
                <Typography sx={bodyText}>
                  Il Servizio è destinato a professionisti sanitari e
                  organizzazioni e non si rivolge a minori di 16 anni. Non
                  raccogliamo consapevolmente dati personali da minori di 16
                  anni. Se sei un genitore o tutore e vieni a conoscenza del
                  fatto che un minore ci ha fornito dati personali, contattaci:
                  provvederemo a rimuoverli immediatamente.
                </Typography>
              </Section>

              <Section id="cookie" title="17. Cookie">
                <Typography sx={bodyText}>
                  Il sito medlift.app utilizza cookie tecnici necessari al
                  funzionamento (es. mantenimento della sessione di accesso)
                  e, con il tuo consenso, cookie analitici aggregati. Puoi
                  gestire le preferenze attraverso le impostazioni del tuo
                  browser. L'app mobile non utilizza cookie ma può fare uso di
                  identificatori tecnici equivalenti generati dai servizi di
                  terze parti elencati alla sezione 9.
                </Typography>
              </Section>

              <Section id="modifiche" title="18. Modifiche all'Informativa">
                <Typography sx={bodyText}>
                  Potremmo aggiornare questa Privacy Policy per riflettere
                  modifiche operative, legali o regolamentari. La data
                  dell'ultimo aggiornamento è indicata in cima a questa pagina.
                  In caso di modifiche sostanziali ti informeremo tramite email
                  o notifica in-app prima che le nuove disposizioni entrino in
                  vigore.
                </Typography>
              </Section>

              <Section id="contatti" title="19. Contatti">
                <Typography sx={bodyText}>
                  Per qualsiasi domanda, richiesta o reclamo relativo a questa
                  Privacy Policy o al trattamento dei tuoi dati personali puoi
                  scriverci a:
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    mt: 1,
                    borderRadius: 3,
                    border: `1px solid ${BRAND.border}`,
                    bgcolor: BRAND.bgSoft,
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "flex-start", sm: "center" },
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: '"Inter", sans-serif',
                        fontWeight: 600,
                        color: BRAND.ink,
                        fontSize: "1rem",
                      }}
                    >
                      MedLift — Tutela della privacy
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: '"Inter", sans-serif',
                        color: BRAND.muted,
                        fontSize: "0.9rem",
                        mt: 0.5,
                      }}
                    >
                      Risposta garantita entro 30 giorni
                    </Typography>
                  </Box>
                  <MuiLink
                    href={`mailto:${contactEmail}`}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      bgcolor: BRAND.teal,
                      color: BRAND.ink,
                      fontFamily: '"Inter", sans-serif',
                      fontWeight: 600,
                      fontSize: "0.95rem",
                      px: 2.5,
                      py: 1.25,
                      borderRadius: 2,
                      textDecoration: "none",
                      transition: "background 0.15s ease",
                      "&:hover": { bgcolor: BRAND.tealDark },
                    }}
                  >
                    {contactEmail}
                  </MuiLink>
                </Paper>
              </Section>

              <Divider sx={{ my: 4, borderColor: BRAND.border }} />
              <Typography
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  color: BRAND.muted,
                  fontSize: "0.85rem",
                  textAlign: "center",
                }}
              >
                Documento in vigore dal {EFFECTIVE_DATE}. Versione bilingue
                disponibile su richiesta.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      <MiniFooter contactEmail={contactEmail} onNavigate={goTo} />
    </Box>
  );
}
